import { Customer, InvitationDetails } from '../types';

/**
 * The only `verifiedCustomer` value that counts as verified. The field also
 * holds 'Inbjuden' (the default for a newly created customer) and 'Delad'.
 *
 * Customers that are not verified must not be selectable when a project is
 * created — the API rejects them, so offering them would only produce a
 * confusing error on submit. They are still listed (disabled) so the company
 * can see that the customer exists and is waiting for verification.
 */
export const VERIFIED_CUSTOMER = 'Verifierad';

export const isVerifiedCustomer = (customer: Customer): boolean =>
  customer.verifiedCustomer === VERIFIED_CUSTOMER;

/**
 * Whether `customerId` may be submitted when creating a project: it must be one
 * of the loaded customers, and that customer must be verified.
 */
export const isSelectableCustomerId = (
  customers: Customer[],
  customerId: string
): boolean =>
  customers.some(customer => customer.id === customerId && isVerifiedCustomer(customer));

/**
 * Options for the project customer picker — every customer, verified or not.
 * The picker renders unverified ones as disabled options.
 *
 * The selected customer is always kept in the list, even when it does not match
 * the search string. A native <select> silently resets its value when the
 * matching <option> disappears, and that reset does not fire onChange — so the
 * React state would keep a customer the user can no longer see selected.
 */
export const customerPickerOptions = (
  customers: Customer[],
  search: string,
  selectedCustomerId: string
): Customer[] => {
  const query = search.trim().toLowerCase();
  if (!query) return customers;
  return customers.filter(
    customer =>
      customer.id === selectedCustomerId ||
      customer.name.toLowerCase().includes(query) ||
      customer.email.toLowerCase().includes(query)
  );
};

/**
 * A customer invitation that has not been accepted yet has no customer row, so
 * `/companies/me/customers` does not return it. The picker still lists these as
 * disabled options: to the company they are "invited customers" too.
 */
const ACTIVE_INVITATION_STATUSES: InvitationDetails['status'][] = ['pending', 'sent'];

export const invitationName = (invitation: InvitationDetails): string =>
  invitation.customerName || invitation.contactName || '';

export const invitationEmail = (invitation: InvitationDetails): string =>
  (invitation.customerEmail || invitation.contactEmail || '').trim();

/**
 * Open invitations that are not yet customers. Revoked and used links are
 * dropped, as is any invitation whose email already belongs to a customer, so
 * nobody is listed twice.
 */
export const pendingCustomerInvitations = (
  invitations: InvitationDetails[],
  customers: Customer[]
): InvitationDetails[] => {
  const customerEmails = new Set(customers.map(customer => (customer.email || '').trim().toLowerCase()));
  return invitations.filter(
    invitation =>
      ACTIVE_INVITATION_STATUSES.includes(invitation.status) &&
      invitation.linkState !== 'revoked' &&
      invitation.linkState !== 'used' &&
      !customerEmails.has(invitationEmail(invitation).toLowerCase())
  );
};

export const invitationPickerOptions = (
  invitations: InvitationDetails[],
  search: string
): InvitationDetails[] => {
  const query = search.trim().toLowerCase();
  if (!query) return invitations;
  return invitations.filter(
    invitation =>
      invitationName(invitation).toLowerCase().includes(query) ||
      invitationEmail(invitation).toLowerCase().includes(query)
  );
};
